/**
 * useVault — React hooks for HupperVault interaction.
 *
 * - useVaultBalance: in-vault USDT balance (custodial)
 * - useUSDTBalance: wallet-held USDT (not in vault)
 * - useVaultActions: deposit / withdraw / approve / mint mocked USDT
 *
 * All hooks require an injected EIP-1193 provider (window.ethereum).
 * Returns a safe default if vault is not yet deployed on the active chain.
 */
import { useCallback, useEffect, useState } from "react";
import type { Eip1193Provider } from "ethers";
import { useWallets } from "@privy-io/react-auth";
import {
  ContractNotDeployedError,
  getReadClient,
  getWriteClient,
} from "../contracts/VaultClient";
import { ACTIVE_CHAIN, isVaultDeployed } from "../contracts/chains";

interface BalanceState {
  loading: boolean;
  value: bigint;
  error: Error | null;
}

const ZERO: BalanceState = { loading: false, value: 0n, error: null };

function getInjected(): Eip1193Provider | null {
  if (typeof window === "undefined") return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).ethereum ?? null;
}

/**
 * In-vault USDT balance (the custodial deposit users hold inside HupperVault).
 * Updates on `pollIntervalMs` (default 12s) plus optional manual refetch via `refetch`.
 */
export function useVaultBalance(
  user: string | null,
  pollIntervalMs = 12_000
): BalanceState & { refetch: () => Promise<void> } {
  const [state, setState] = useState<BalanceState>({ ...ZERO, loading: !!user });

  const fetch = useCallback(async () => {
    if (!user || !isVaultDeployed()) {
      setState(ZERO);
      return;
    }
    try {
      const client = getReadClient();
      const value = await client.vaultBalanceOf(user);
      setState({ loading: false, value, error: null });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      if (error instanceof ContractNotDeployedError) {
        setState(ZERO);
      } else {
        setState({ loading: false, value: 0n, error });
      }
    }
  }, [user]);

  useEffect(() => {
    fetch();
    if (!user || pollIntervalMs <= 0) return;
    const id = setInterval(fetch, pollIntervalMs);
    return () => clearInterval(id);
  }, [fetch, user, pollIntervalMs]);

  return { ...state, refetch: fetch };
}

/**
 * Wallet-held USDT balance (not deposited in vault yet).
 */
export function useUSDTBalance(
  user: string | null,
  pollIntervalMs = 12_000
): BalanceState & { refetch: () => Promise<void> } {
  const [state, setState] = useState<BalanceState>({ ...ZERO, loading: !!user });

  const fetch = useCallback(async () => {
    if (!user || !isVaultDeployed()) {
      setState(ZERO);
      return;
    }
    try {
      const client = getReadClient();
      const value = await client.usdtBalanceOf(user);
      setState({ loading: false, value, error: null });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      if (error instanceof ContractNotDeployedError) {
        setState(ZERO);
      } else {
        setState({ loading: false, value: 0n, error });
      }
    }
  }, [user]);

  useEffect(() => {
    fetch();
    if (!user || pollIntervalMs <= 0) return;
    const id = setInterval(fetch, pollIntervalMs);
    return () => clearInterval(id);
  }, [fetch, user, pollIntervalMs]);

  return { ...state, refetch: fetch };
}

interface ActionState {
  pending: boolean;
  error: Error | null;
  txHash: string | null;
}

const ACTION_IDLE: ActionState = { pending: false, error: null, txHash: null };

/**
 * Vault state-changing actions. Returns functions + status state.
 *
 * Usage:
 * ```
 * const { deposit, approve, withdraw, status } = useVaultActions();
 * await approve(parseUnits("100", 6));
 * await deposit(parseUnits("100", 6));
 * ```
 */
export function useVaultActions() {
  const [status, setStatus] = useState<ActionState>(ACTION_IDLE);
  const { wallets } = useWallets();

  const resolveProvider = useCallback(async (): Promise<Eip1193Provider | null> => {
    // Prefer the Privy embedded wallet (no extension prompt).
    const embedded = wallets.find((w) => w.walletClientType === "privy");
    const wallet = embedded ?? wallets[0];
    if (wallet) {
      try {
        await wallet.switchChain(ACTIVE_CHAIN.chainId);
      } catch {
        // chain may already be active or wallet may not support this; continue
      }
      return (await wallet.getEthereumProvider()) as unknown as Eip1193Provider;
    }
    return getInjected();
  }, [wallets]);

  const runWrite = useCallback(
    async (label: string, fn: (client: Awaited<ReturnType<typeof getWriteClient>>) => Promise<{ hash?: string } | null>) => {
      if (!isVaultDeployed()) {
        const err = new Error(`HupperVault not deployed on ${ACTIVE_CHAIN.name}`);
        setStatus({ pending: false, error: err, txHash: null });
        throw err;
      }
      const provider = await resolveProvider();
      if (!provider) {
        const err = new Error("No wallet available (login first or install MetaMask)");
        setStatus({ pending: false, error: err, txHash: null });
        throw err;
      }

      setStatus({ pending: true, error: null, txHash: null });
      try {
        const client = await getWriteClient(provider);
        const receipt = await fn(client);
        const txHash = receipt?.hash ?? null;
        setStatus({ pending: false, error: null, txHash });
        return receipt;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setStatus({ pending: false, error, txHash: null });
        // eslint-disable-next-line no-console
        console.error(`[useVaultActions:${label}]`, error);
        throw error;
      }
    },
    [resolveProvider]
  );

  const approve = useCallback(
    (amount: bigint) => runWrite("approve", (c) => c.approveUSDT(amount)),
    [runWrite]
  );

  const deposit = useCallback(
    (amount: bigint) => runWrite("deposit", (c) => c.deposit(amount)),
    [runWrite]
  );

  const withdraw = useCallback(
    (amount: bigint) => runWrite("withdraw", (c) => c.withdraw(amount)),
    [runWrite]
  );

  const sendUSDT = useCallback(
    (to: string, amount: bigint) => runWrite("sendUSDT", (c) => c.transferUSDT(to, amount)),
    [runWrite]
  );

  const mintMockUSDT = useCallback(
    (to: string, amount: bigint) =>
      runWrite("mintMockUSDT", (c) => c.mintMockUSDT(to, amount)),
    [runWrite]
  );

  return { approve, deposit, withdraw, sendUSDT, mintMockUSDT, status };
}

/**
 * Format a USDT bigint (6 decimals) into a human string with 2 decimal places.
 * Examples: 100_000_000n → "100.00", 50_500_000n → "50.50"
 */
export function formatUSDT(amount: bigint): string {
  const whole = amount / 1_000_000n;
  const frac = amount % 1_000_000n;
  const fracStr = frac.toString().padStart(6, "0").slice(0, 2);
  return `${whole}.${fracStr}`;
}

/**
 * Parse a human USDT amount into a bigint (6 decimals).
 * Throws on non-numeric or negative input.
 */
export function parseUSDT(amount: number | string): bigint {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  if (!Number.isFinite(n) || n < 0) throw new Error(`Invalid USDT amount: ${amount}`);
  return BigInt(Math.round(n * 1_000_000));
}
